/**
 * Scene preloading – background queue that preloads ALL scenes in story order,
 * with priority boosting for scenes the user is approaching.
 *
 * Strategy:
 * 1. Fonts: preloaded via <link rel="preload"> in HTML (fastest possible)
 * 2. Scenes: all scenes queued in story order, processed one by one
 * 3. Non-scene assets (atmo, music, sfx, global audio): interleaved with
 *    the scene where they first become relevant (based on playFromTo ranges)
 *
 * When gameplay progresses, upcoming scenes are bumped to the front of the
 * queue so they are ready even if background preload hasn't caught up yet.
 */

import type { SceneData, ChoiceCondition } from '../scenes/types'
import {
  assetUrl,
  getDefaultBgPath,
  getBubbleImagePath,
  getSceneImagePath,
  getChoiceImagePath,
  getChoiceBgPath,
  getChoiceBorderPath,
  getChoiceBorderActivePath,
  collectConditionalBgUrls,
  toHalfSizeUrl,
  buildSrcset,
  isSlowNetwork,
  resolveTextureUrl,
} from '../../utils/pathResolver'
import { getAudioManager } from '../audio'
import { debugLog } from '../debug'

const PRIORITY_AHEAD = 4

/**
 * Decide whether we should preload the half-size variant instead of full.
 * Uses the same heuristics as the browser's srcset selection:
 * viewport width × DPR × scene proportion.
 * On slow networks, always prefer half-size.
 */
function shouldPreloadHalf(): boolean {
  if (isSlowNetwork()) return true
  const vw = window.innerWidth
  const dpr = window.devicePixelRatio || 1
  return vw * dpr * 0.95 < 750
}

/**
 * Map an image URL to the version the browser will actually render.
 * Only affects URLs that have a half-size variant (scene/images PNGs/JPGs).
 */
function toPreloadUrl(url: string): string {
  if (!shouldPreloadHalf()) return url
  if (!buildSrcset(url)) return url
  return toHalfSizeUrl(url)
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PreloadContext {
  scenes: SceneData[]
  hasLoaded: (sceneId: string) => boolean
  hasPreloaded: (sceneId: string) => boolean
  markPreloaded: (sceneId: string) => void
  evaluateCondition: (condition: ChoiceCondition) => boolean
  audioConfig?: AudioPreloadConfig
}

export interface AudioPreloadConfig {
  atmo?: { tracks: Array<{ name: string; playFromTo: [string | string[], string | string[]] }> }
  music?: { tracks: Array<{ name: string; playFromTo: [string | string[], string | string[]] }> }
  sfx?: Array<{ name: string | string[]; playFromTo: [string | string[], string | string[]] }>
  rain?: { soft: string; strong: string }
  global?: { intro?: string; outro?: string }
  ui?: { buttonClick: string; choice: string }
  /** First scene where rain effect starts (derived from effects.rain config) */
  rainStartScene?: string
}

// ---------------------------------------------------------------------------
// Scene asset collection
// ---------------------------------------------------------------------------

function collectImageUrls(scene: SceneData): string[] {
  const urls: string[] = []

  if (scene.type !== 'chapter-title' && scene.type !== 'homework' && scene.type !== 'epilogue') {
    if (scene.bg === false) {
      // Explicitly no bg – skip
    } else if (Array.isArray(scene.bg)) {
      urls.push(...collectConditionalBgUrls(scene.id, scene.bg))
    } else {
      urls.push(scene.bg || getDefaultBgPath(scene.id))
    }
  }

  if (scene.speechBubbles) {
    for (const bubble of scene.speechBubbles) {
      urls.push(getBubbleImagePath(scene.id, bubble.id))
    }
  }

  if (scene.images) {
    for (const img of scene.images) {
      urls.push(img.image.startsWith('/') ? assetUrl(img.image) : getSceneImagePath(scene.id, img.image))
    }
  }

  if (scene.animations) {
    for (const anim of scene.animations) {
      urls.push(getSceneImagePath(scene.id, anim.image))
    }
  }

  if (scene.layout?.config?.images) {
    for (const img of scene.layout.config.images) {
      urls.push(getSceneImagePath(scene.id, img.image))
    }
  }

  if (scene.layout?.config?.image?.src) {
    urls.push(getSceneImagePath(scene.id, scene.layout.config.image.src))
  }

  const background = (scene as unknown as Record<string, unknown>).background as string | undefined
  if (scene.type === 'chapter-title' && background) {
    urls.push(resolveTextureUrl(background))
  }

  if (scene.choices) {
    urls.push(getChoiceImagePath(scene.id, 1))
    urls.push(getChoiceImagePath(scene.id, 2))
    urls.push(getChoiceBgPath(1))
    urls.push(getChoiceBgPath(2))
    urls.push(getChoiceBorderPath(1))
    urls.push(getChoiceBorderPath(2))
    urls.push(getChoiceBorderActivePath(1))
    urls.push(getChoiceBorderActivePath(2))
  }

  return urls
}

function collectAudioUrls(scene: SceneData): string[] {
  const urls: string[] = []
  const base = assetUrl(`/assets/scenes/${scene.id}`)
  const am = getAudioManager()

  if (scene.speechBubbles) {
    for (const bubble of scene.speechBubbles) {
      const match = bubble.id.match(/bubble-(\d+)/)
      const num = match ? match[1] : bubble.id.replace('bubble-', '')
      urls.push(`${base}/${am.resolveIndexedPattern(am.getBubblePattern(), num)}`)
    }
  }

  if (scene.type === 'chapter-title') {
    urls.push(`${base}/${am.getChapterTitleSound()}`)
  }

  if (scene.choices) {
    urls.push(`${base}/${am.resolveIndexedPattern(am.getChoicePattern(), 1)}`)
    urls.push(`${base}/${am.resolveIndexedPattern(am.getChoicePattern(), 2)}`)
  }

  return urls
}

// ---------------------------------------------------------------------------
// Non-scene asset collection (atmo, music, sfx, global audio)
// ---------------------------------------------------------------------------

const preloadedExtras = new Set<string>()

function firstSceneId(spec: string | string[]): string {
  return typeof spec === 'string' ? spec : spec[0]
}

/**
 * Collect non-scene audio URLs that should be preloaded alongside a given scene.
 * Each audio file is preloaded once, when we reach the scene where it first
 * becomes relevant (the start of its playFromTo range).
 */
function collectNonSceneAssets(
  sceneId: string,
  sceneIndex: number,
  totalScenes: number,
  audioConfig?: AudioPreloadConfig,
): string[] {
  if (!audioConfig) return []
  const urls: string[] = []

  const addOnce = (key: string, url: string) => {
    if (!preloadedExtras.has(key)) {
      preloadedExtras.add(key)
      urls.push(url)
    }
  }

  // First scene: global intro + UI SFX
  if (sceneIndex === 0) {
    if (audioConfig.global?.intro)
      addOnce('global:intro', assetUrl(`/assets/audio/${audioConfig.global.intro}`))
    if (audioConfig.ui?.buttonClick)
      addOnce('ui:buttonClick', assetUrl(`/assets/audio/sfx/${audioConfig.ui.buttonClick}`))
    if (audioConfig.ui?.choice)
      addOnce('ui:choice', assetUrl(`/assets/audio/sfx/${audioConfig.ui.choice}`))
  }

  // Rain audio: preload when rain effect actually starts
  if (audioConfig.rain && audioConfig.rainStartScene === sceneId) {
    if (audioConfig.rain.soft)
      addOnce('rain:soft', assetUrl(`/assets/audio/sfx/${audioConfig.rain.soft}`))
    if (audioConfig.rain.strong)
      addOnce('rain:strong', assetUrl(`/assets/audio/sfx/${audioConfig.rain.strong}`))
  }

  // Near the end: global outro
  if (sceneIndex >= totalScenes - 2 && audioConfig.global?.outro) {
    addOnce('global:outro', assetUrl(`/assets/audio/${audioConfig.global.outro}`))
  }

  // Atmo tracks: preload when their playFromTo range starts
  if (audioConfig.atmo?.tracks) {
    for (const t of audioConfig.atmo.tracks) {
      if (firstSceneId(t.playFromTo[0]) === sceneId)
        addOnce(`atmo:${t.name}`, assetUrl(`/assets/audio/atmo/${t.name}`))
    }
  }

  // Music tracks: preload when their playFromTo range starts
  if (audioConfig.music?.tracks) {
    for (const t of audioConfig.music.tracks) {
      if (firstSceneId(t.playFromTo[0]) === sceneId)
        addOnce(`music:${t.name}`, assetUrl(`/assets/audio/music/${t.name}`))
    }
  }

  // Scroll SFX: preload when their playFromTo range starts
  if (audioConfig.sfx) {
    for (const cfg of audioConfig.sfx) {
      if (firstSceneId(cfg.playFromTo[0]) === sceneId) {
        const names = typeof cfg.name === 'string' ? [cfg.name] : cfg.name
        for (const n of names) addOnce(`sfx:${n}`, assetUrl(`/assets/audio/sfx/${n}`))
      }
    }
  }

  return urls
}

// ---------------------------------------------------------------------------
// Low-priority asset loader with concurrency limiting
// ---------------------------------------------------------------------------

/**
 * Max simultaneous preload fetches. Keeps browser connections free for
 * the game's own DOM image/audio requests which need higher priority.
 */
const MAX_CONCURRENT = 2

let preloadAbortController = new AbortController()
let paused = false

function preloadAsset(src: string): Promise<void> {
  return fetch(src, { priority: 'low', signal: preloadAbortController.signal } as RequestInit)
    .then(res => res.blob())
    .then(() => undefined)
    .catch(() => undefined)
}

/** Yield to the main thread so the browser can process rendering and user input. */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0))
}

// ---------------------------------------------------------------------------
// Scene-level preload
// ---------------------------------------------------------------------------

async function preloadSceneAssets(sceneIndex: number, ctx: PreloadContext, gen: number): Promise<void> {
  if (sceneIndex < 0 || sceneIndex >= ctx.scenes.length) return
  if (gen !== queueGeneration) return

  const scene = ctx.scenes[sceneIndex]
  if (!scene) return
  if (ctx.hasLoaded(scene.id) || ctx.hasPreloaded(scene.id)) return

  ctx.markPreloaded(scene.id)

  const images = collectImageUrls(scene).map(toPreloadUrl)
  const audio = collectAudioUrls(scene)
  const extras = collectNonSceneAssets(scene.id, sceneIndex, ctx.scenes.length, ctx.audioConfig)
  const allUrls = [...images, ...audio, ...extras]

  debugLog(
    `[Preload] Scene "${scene.id}": ${images.length} img, ${audio.length} audio` +
      (extras.length ? `, ${extras.length} shared` : '') +
      ` (${allUrls.length} total)`,
  )

  for (let i = 0; i < allUrls.length; i += MAX_CONCURRENT) {
    if (gen !== queueGeneration || paused) return
    const batch = allUrls.slice(i, i + MAX_CONCURRENT)
    await Promise.all(batch.map(preloadAsset))
    if (i + MAX_CONCURRENT < allUrls.length) await yieldToMain()
  }

  debugLog(`[Preload] Scene "${scene.id}": done`)
}

// ---------------------------------------------------------------------------
// Queue – background preload with priority boosting
// ---------------------------------------------------------------------------

let backgroundQueue: number[] = []
let priorityQueue: number[] = []
let running = false
let started = false
let currentCtx: PreloadContext | null = null
let queueGeneration = 0

async function processQueue(): Promise<void> {
  if (running || !currentCtx) return
  running = true
  const gen = queueGeneration

  while ((priorityQueue.length > 0 || backgroundQueue.length > 0) && gen === queueGeneration && !paused) {
    const idx =
      priorityQueue.length > 0 ? priorityQueue.shift()! : backgroundQueue.shift()!

    await preloadSceneAssets(idx, currentCtx!, gen)
  }

  if (gen === queueGeneration && !paused) {
    running = false
    debugLog('[Preload] All queued scenes preloaded')
  }
}

/**
 * Start background preloading of ALL scenes in story order.
 * Subsequent calls are no-ops until resetPreloadState() is called.
 */
function startBackgroundPreload(ctx: PreloadContext): void {
  if (started) return
  started = true
  currentCtx = ctx

  for (let i = 0; i < ctx.scenes.length; i++) {
    const scene = ctx.scenes[i]
    if (scene && !ctx.hasLoaded(scene.id) && !ctx.hasPreloaded(scene.id)) {
      backgroundQueue.push(i)
    }
  }

  debugLog(`[Preload] Background preload queued: ${backgroundQueue.length} scenes`)
  processQueue()
}

/**
 * Bump upcoming scenes to the front of the queue so they load next,
 * regardless of where the background preloader currently is.
 */
function boostPriority(fromIndex: number, ctx: PreloadContext): void {
  currentCtx = ctx
  let idx = fromIndex + 1
  let added = 0

  while (added < PRIORITY_AHEAD && idx < ctx.scenes.length) {
    const scene = ctx.scenes[idx]
    if (scene && !ctx.hasLoaded(scene.id) && !ctx.hasPreloaded(scene.id) && !priorityQueue.includes(idx)) {
      const bgPos = backgroundQueue.indexOf(idx)
      if (bgPos !== -1) backgroundQueue.splice(bgPos, 1)
      priorityQueue.push(idx)
      added++
    }
    idx++
  }

  processQueue()
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start full background preload of all scenes (called once on app init).
 * Scenes are preloaded in story order, one by one.
 */
export function preloadInitialScenes(ctx: PreloadContext): void {
  startBackgroundPreload(ctx)
}

/**
 * Ensure background preload is running and boost upcoming scenes.
 * Called each time a new scene loads during gameplay.
 */
export function triggerPreloadAhead(fromIndex: number, ctx: PreloadContext): void {
  startBackgroundPreload(ctx)
  boostPriority(fromIndex, ctx)
}

/**
 * Pause background preloading and abort in-flight fetches.
 * Frees browser connections for high-priority assets (e.g. bubble images).
 */
export function pausePreload(): void {
  paused = true
  preloadAbortController.abort()
  preloadAbortController = new AbortController()
}

/**
 * Resume background preloading after a pause.
 */
export function resumePreload(): void {
  if (!paused) return
  paused = false
  running = false
  processQueue()
}

/**
 * Clear queue and state (called on engine reset).
 */
export function resetPreloadState(): void {
  queueGeneration++
  backgroundQueue = []
  priorityQueue = []
  running = false
  started = false
  currentCtx = null
  paused = false
  preloadAbortController.abort()
  preloadAbortController = new AbortController()
}
